/**
 * Community categories.
 *
 * `community/list` returns a derived `categories` array per community: the
 * distinct strings across its contents' `content.categories` JSON column. There
 * is no category entity, id or whitelist on the backend — the values are
 * free-form (uppercase by convention, e.g. "STRENGTH"), so the filter chips are
 * whatever the user's own communities actually carry, not a fixed list.
 *
 * A community with no categorised content comes back with `[]` and therefore
 * only ever shows under "Todas".
 */

import { getIntlLocale } from '@i18n';

// Sentinel for the "no filter" chip. Not a value the backend can return.
export const ALL_CATEGORIES = '__all__';

/** Display label for a raw value: "CORE_STRENGTH" → "Core strength". */
export function categoryLabel(value) {
    const words = String(value || '').replace(/[_-]+/g, ' ').trim().toLowerCase();
    if (!words) return '';
    return words.charAt(0).toUpperCase() + words.slice(1);
}

/** Every distinct category across `communities`, ordered by display label. */
export function collectCategories(communities) {
    const seen = new Set();

    (communities || []).forEach(community => {
        (community?.categories || []).forEach(raw => {
            const value = String(raw ?? '').trim();
            if (value) seen.add(value);
        });
    });

    return [...seen].sort((a, b) => categoryLabel(a).localeCompare(categoryLabel(b), getIntlLocale()));
}

/** Whether `community` carries `category` (ALL_CATEGORIES matches everything). */
export function hasCategory(community, category) {
    if (category === ALL_CATEGORIES) return true;
    return (community?.categories || []).some(value => String(value) === String(category));
}
